import { useState, useEffect, useCallback } from 'react';
import { ArrowLeftRight, Plus, Bell, Wallet, Loader2, Building, ArrowDownToLine, ArrowUpRight, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import { usePlaidLink } from 'react-plaid-link';
import api from '../api';

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass" style={{ padding: '0.8rem', border: '1px solid var(--surface-border)', borderLeft: `4px solid ${payload[0].payload.color}`, fontSize: '0.9rem', backgroundColor: 'var(--surface)', boxShadow: '0 4px 6px rgba(0,0,0,0.3)', zIndex: 100 }}>
        <p style={{ margin: '0 0 0.25rem 0', fontWeight: 600, color: 'var(--text-main)' }}>{payload[0].name}</p>
        <p style={{ margin: 0, color: 'var(--text-main)', fontWeight: 'bold', fontSize: '1.1rem' }}>
          ${payload[0].value.toLocaleString(undefined, {minimumFractionDigits: 2})}
        </p>
      </div>
    );
  }
  return null;
};

function timeSince(dateStr) {
  if (!dateStr) return 'Never';
  const seconds = Math.floor((new Date() - new Date(dateStr)) / 1000);
  let interval = seconds / 31536000;
  if (interval > 1) return Math.floor(interval) + " years ago";
  interval = seconds / 2592000;
  if (interval > 1) return Math.floor(interval) + " months ago";
  interval = seconds / 86400;
  if (interval > 1) return Math.floor(interval) + " days ago";
  interval = seconds / 3600;
  if (interval > 1) return Math.floor(interval) + " hours ago";
  interval = seconds / 60;
  if (interval > 1) return Math.floor(interval) + " mins ago";
  return "just now";
}

export default function Dashboard() {
  const [incomes, setIncomes] = useState([]);
  const [totalIncome, setTotalIncome] = useState(0);
  const [funds, setFunds] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [allocations, setAllocations] = useState([]);
  
  const [plaidStatus, setPlaidStatus] = useState({ linked: false, bank: null, transactions: [] });
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [linkToken, setLinkToken] = useState(null);

  const [resetConfirming, setResetConfirming] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [ledgerFilterIncomeId, setLedgerFilterIncomeId] = useState('ALL');

  const fetchDashboardData = useCallback(async () => {
    try {
      const [incomesRes, plaidRes, fundsRes, ledgerRes, allocRes] = await Promise.all([
        api.get('/incomes'),
        api.get('/plaid/status'),
        api.get('/funds'),
        api.get('/ledger'),
        api.get('/allocations')
      ]);
      
      const total = incomesRes.data.reduce((sum, inc) => {
        let monthlyAmt = parseFloat(inc.amount || 0);
        const freq = (inc.frequency || 'bi-weekly').toLowerCase();
        if (freq === 'weekly') monthlyAmt *= (52 / 12);
        else if (freq === 'bi-weekly') monthlyAmt *= (26 / 12);
        else if (freq === 'semi-monthly') monthlyAmt *= 2;
        return sum + monthlyAmt;
      }, 0);
      setIncomes(incomesRes.data);
      setTotalIncome(total);
      setPlaidStatus(plaidRes.data);
      setFunds(fundsRes.data);
      setLedger(ledgerRes.data);
      setAllocations(allocRes.data);



      // If not linked or in error state, get a link token for Plaid
      if (!plaidRes.data.linked || plaidRes.data.error) {
        try {
          const tokenRes = await api.post('/plaid/create-link-token', { update: !!plaidRes.data.error });
          setLinkToken(tokenRes.data.link_token);
        } catch (err) {
          console.error('Failed to create link token', err);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    window.addEventListener('thread_settings_updated', fetchDashboardData);
    return () => window.removeEventListener('thread_settings_updated', fetchDashboardData);
  }, [fetchDashboardData]);

  const onSuccess = useCallback(async (public_token, metadata) => {
    setLinking(true);
    try {
      await api.post('/plaid/exchange-public-token', { public_token });
      fetchDashboardData();
    } catch (err) {
      console.error('Exchange failed', err);
    } finally {
      setLinking(false);
    }
  }, [fetchDashboardData]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
  });

  const handleLinkBank = () => {
    if (ready) {
      open();
    }
  };

  const handleDisconnect = async () => {
    if (!confirmDisconnect) {
      setConfirmDisconnect(true);
      return;
    }
    try {
      await api.post('/plaid/disconnect');
      setConfirmDisconnect(false);
      fetchDashboardData();
    } catch (err) {
      toast.error('Failed to disconnect bank');
    }
  };

  const handleDashboardAutopilot = async () => {
    setSimulating(true);
    try {
      await api.post('/allocations/global-smart-allocate');
      fetchDashboardData();
    } catch (err) {
      toast.error('Error during Autopilot setup');
      setSimulating(false);
    }
  };

  const handleForceSync = async () => {
    setSyncing(true);
    try {
      await api.post('/plaid/sync');
      await fetchDashboardData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to sync with Plaid');
    } finally {
      setSyncing(false);
    }
  };



  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Loader2 className="text-muted" style={{ animation: 'spin 2s linear infinite' }} /></div>;
  }

  return (
    <div className="animate-fade-in responsive-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--spacing-md)' }}>
      
      {/* Main Column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
        
        {/* Zero-State Autopilot Card */}
        {allocations.length === 0 && incomes.length > 0 && funds.length > 0 && (
          <div className="glass" style={{ padding: 'var(--spacing-xl)', textAlign: 'center', border: '1px solid var(--primary)', background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(192, 132, 252, 0.05) 100%)' }}>
            <h2 style={{ marginBottom: '1rem', color: 'var(--text-main)' }}>Let's organize your paycheck.</h2>
            <p className="text-muted" style={{ marginBottom: '2rem', fontSize: '1.1rem', maxWidth: '500px', margin: '0 auto 2rem auto' }}>
              You haven't set up any routing rules yet. Let Thread Finance automatically analyze your targets and set up your buckets.
            </p>
            <button 
              className="btn btn-primary" 
              onClick={handleDashboardAutopilot}
              disabled={simulating}
              style={{ padding: '1rem 2rem', fontSize: '1.1rem', borderRadius: '3rem', background: 'linear-gradient(to right, #4f46e5, #7c3aed)', border: 'none', boxShadow: '0 0 20px rgba(59, 130, 246, 0.3)' }}
            >
              {simulating ? <Loader2 size={20} className="spin" /> : 'Auto-Setup My Paycheck'}
            </button>
            <div style={{ marginTop: '1rem' }}>
              <Link to="/allocations" style={{ color: 'var(--text-muted)', fontSize: '0.9rem', textDecoration: 'underline' }}>Or set up rules manually</Link>
            </div>
          </div>
        )}

        {/* Over-Allocated Warning Banner */}
        {incomes.some(inc => {
          const fixedSum = allocations.filter(a => a.income_id === inc.id && a.type === 'fixed').reduce((sum, a) => sum + parseFloat(a.amount), 0);
          return fixedSum > inc.amount;
        }) && (
          <div className="glass" style={{ padding: '1.25rem', background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <AlertCircle color="var(--danger)" style={{ flexShrink: 0 }} size={24} />
            <div>
              <strong style={{ display: 'block', color: 'var(--danger)', marginBottom: '0.25rem', fontSize: '1.1rem' }}>Warning: Over-Budget</strong>
              <p style={{ margin: 0, color: 'var(--text-muted)' }}>
                Your current rules are trying to move more money than you actually earn. Please <Link to="/allocations" style={{ color: 'var(--danger)', textDecoration: 'underline' }}>adjust your targets</Link> or re-run the Auto-Setup.
              </p>
            </div>
          </div>
        )}

        {/* Bank Connection Status / Balance Overview */}
        <div className="glass" style={{ padding: 'var(--spacing-lg)' }}>
          <h2 style={{ margin: '0 0 1rem 0' }}>Balance Overview</h2>
          
          {plaidStatus.error ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--danger)' }}>
                <AlertCircle size={18} />
                <span>Bank Connection Broken. Please repair.</span>
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button className="btn btn-primary" onClick={() => handleLinkBank()} disabled={linking || !ready}>
                  {linking ? <Loader2 size={18} style={{ animation: 'spin 2s linear infinite' }} /> : <RefreshCw size={18} />}
                  Repair Connection
                </button>
              </div>
            </div>
          ) : plaidStatus.linked ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              
              {/* Math Breakdown Box */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', background: 'rgba(0,0,0,0.2)', padding: '1.5rem', borderRadius: 'var(--radius-md)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="text-muted" style={{ fontSize: '1.1rem' }}>Checking Balance</span>
                  <strong style={{ fontSize: '1.2rem', color: 'var(--text-main)' }}>
                    ${plaidStatus.bank?.balance?.toLocaleString(undefined, {minimumFractionDigits: 2}) || '0.00'}
                  </strong>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="text-muted" style={{ fontSize: '1.1rem' }}>Saved in Buckets</span>
                  <strong style={{ fontSize: '1.2rem', color: 'var(--danger)' }}>
                    -${funds.reduce((sum, f) => sum + parseFloat(f.current_balance || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </strong>
                </div>

                <div style={{ height: '1px', backgroundColor: 'var(--surface-border)', margin: '0.5rem 0' }}></div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong style={{ fontSize: '1.3rem', display: 'block', color: 'var(--text-main)' }}>Safe to Spend</strong>
                    <span className="text-muted" style={{ fontSize: '0.9rem' }}>(Leftover)</span>
                  </div>
                  <h2 style={{ margin: 0, fontSize: '1.8rem', color: (plaidStatus.bank?.balance - funds.reduce((sum, f) => sum + parseFloat(f.current_balance || 0), 0)) < 0 ? 'var(--danger)' : 'var(--success)' }}>
                    ${((plaidStatus.bank?.balance || 0) - funds.reduce((sum, f) => sum + parseFloat(f.current_balance || 0), 0)).toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </h2>
                </div>
              </div>

              {/* Status and Actions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                  <CheckCircle2 size={16} color="var(--success)" />
                  <span>{plaidStatus.bank?.bankName || 'Bank'} (...{plaidStatus.bank?.mask || '0000'})</span>
                  <span style={{ opacity: 0.5 }}>•</span>
                  <span>Last synced: {timeSince(plaidStatus.last_sync)}</span>
                </div>
                
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button 
                    onClick={handleForceSync} 
                    disabled={syncing}
                    className="btn btn-outline"
                    style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    <RefreshCw size={14} style={Object.assign({ marginRight: '0.5rem' }, syncing ? { animation: 'spin 2s linear infinite' } : {})} />
                    {syncing ? 'Syncing...' : 'Sync Now'}
                  </button>
                </div>
              </div>

            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="text-muted">Link your checking account to enable automated paycheck routing.</span>
              <button className="btn btn-primary" onClick={() => handleLinkBank()} disabled={linking || !ready}>
                {linking ? <Loader2 size={18} style={{ animation: 'spin 2s linear infinite' }} /> : <Building size={18} />}
                {linking ? 'Authenticating...' : (!ready ? 'Loading Plaid...' : 'Link Bank Account')}
              </button>
            </div>
          )}
        </div>


        {/* Recent Transfers */}
        <div className="glass" style={{ padding: 'var(--spacing-lg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h3 style={{ margin: 0 }}>Recent Transfers</h3>
            {incomes.length > 0 && ledger.length > 0 && (
              <select 
                value={ledgerFilterIncomeId} 
                onChange={(e) => setLedgerFilterIncomeId(e.target.value)}
                style={{ width: 'auto', padding: '0.4rem 2rem 0.4rem 0.8rem', background: 'rgba(255,255,255,0.05)', borderRadius: 'var(--radius-sm)' }}
              >
                <option value="ALL">All Income Sources</option>
                {incomes.map(inc => (
                  <option key={inc.id} value={inc.id}>{inc.name}</option>
                ))}
              </select>
            )}
          </div>
          
          {ledger.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
              No automated deposits yet. Link your bank and wait for your next paycheck!
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {ledger.filter(entry => ledgerFilterIncomeId === 'ALL' || entry.income_id === ledgerFilterIncomeId).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                  No automated deposits for this income source.
                </div>
              ) : (
                ledger.filter(entry => ledgerFilterIncomeId === 'ALL' || entry.income_id === ledgerFilterIncomeId).map((entry, idx, filteredArr) => {
                  const isExpense = parseFloat(entry.amount) < 0;
                  const isUnallocated = !entry.fund_name && !isExpense;
                  
                  let displayName = entry.fund_name || 'Unknown';
                  let iconColor = entry.fund_color || 'var(--primary)';
                  let subtitle = '';
                  let amountStr = '';
                  let amountColor = 'var(--text-main)';
                  let IconComponent = ArrowDownToLine;

                  if (isExpense) {
                    // Bill Payment (Money left the bucket)
                    iconColor = 'var(--danger)';
                    amountColor = 'var(--text-main)';
                    amountStr = `-$${Math.abs(parseFloat(entry.amount)).toFixed(2)}`;
                    subtitle = `Auto-paid bill on ${new Date(entry.created_at).toLocaleDateString()}`;
                    IconComponent = ArrowUpRight; // or just ArrowLeftRight if imported, let's check imports
                  } else if (isUnallocated) {
                    // Paycheck Remainder
                    displayName = 'Left in Checking (Safe to Spend)';
                    iconColor = 'var(--success)';
                    amountColor = 'var(--success)';
                    amountStr = `+$${Math.abs(parseFloat(entry.amount)).toFixed(2)}`;
                    subtitle = `Remaining deposit from ${entry.income_name || incomes.find(i => i.id === entry.income_id)?.name || 'paycheck'} on ${new Date(entry.created_at).toLocaleDateString()}`;
                    IconComponent = Wallet;
                  } else {
                    // Bucket Funding
                    amountColor = 'var(--success)';
                    amountStr = `+$${Math.abs(parseFloat(entry.amount)).toFixed(2)}`;
                    subtitle = `Saved from ${entry.income_name || incomes.find(i => i.id === entry.income_id)?.name || 'paycheck'} on ${new Date(entry.created_at).toLocaleDateString()}`;
                    IconComponent = ArrowDownToLine;
                  }

                  return (
                  <div key={entry.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '1rem', borderBottom: idx === filteredArr.length - 1 ? 'none' : '1px solid var(--surface-border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: `${iconColor}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: iconColor }}>
                        <IconComponent size={20} />
                      </div>
                      <div>
                        <strong style={{ display: 'block' }}>{displayName}</strong>
                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                          {subtitle}
                        </span>
                      </div>
                    </div>
                    <strong style={{ color: amountColor }}>
                      {amountStr}
                    </strong>
                  </div>
                )})
              )}
            </div>
          )}
        </div>
      </div>

      {/* Side Column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
        {/* Total Income Summary */}
        <div className="glass" style={{ padding: 'var(--spacing-lg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>Overview</h3>
          </div>
          <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: 'var(--radius-sm)', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span className="text-muted">Expected Monthly Income</span>
              <strong>${totalIncome.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
            </div>
          </div>
          <Link to="/allocations" className="btn btn-primary" style={{ width: '100%', textDecoration: 'none', justifyContent: 'center' }}>Manage Paycheck Rules</Link>
        </div>

        {/* Portfolio Visualization */}
        <div className="glass" style={{ padding: 'var(--spacing-lg)' }}>
          <h3 style={{ margin: '0 0 1rem 0' }}>Where Your Money Is</h3>
          
          {funds.filter(f => f.current_balance > 0).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--text-muted)' }}>
              No buckets are funded yet. Wait for your next paycheck!
            </div>
          ) : (
            <div style={{ height: '240px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={funds.filter(f => f.current_balance > 0).map(f => ({ name: f.name, value: f.current_balance, color: f.color }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    stroke="none"
                  >
                    {funds.filter(f => f.current_balance > 0).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color || 'var(--primary)'} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
